import React, { useEffect, useState } from 'react';
import { FaTimes, FaCheck, FaSearch, FaUser, FaTags, FaMapMarkerAlt } from 'react-icons/fa';

import { getComediantes } from '../../../../../components/http/Actions/ComediantesAction';
import { getTags } from '../../../../../components/http/Actions/TagsAction';
import { getLugaresPaginados } from '../../../../../components/http/Actions/LugarEventoAction';

const CreateEventoModal = ({ newEvento, setNewEvento, handleCreateSubmit, isLoading, onClose }) => {
  const [comediantes, setComediantes] = useState([]);
  const [tags, setTags] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [lugaresPagination, setLugaresPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingLugares, setLoadingLugares] = useState(false);
  const [error, setError] = useState('');
  const [searchComediante, setSearchComediante] = useState('');
  const [searchTag, setSearchTag] = useState('');
  const [searchLugar, setSearchLugar] = useState('');
  const [debouncedSearchLugar, setDebouncedSearchLugar] = useState('');
  
  const token = localStorage.getItem("token");

  // Debounce para búsqueda de lugares
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchLugar(searchLugar);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchLugar]);

  // Cargar lugares con paginación
  const fetchLugares = async (page = 1, search = '') => {
    setLoadingLugares(true);
    try {
      await getLugaresPaginados(
        (data) => {
          if (data && data.data) {
            if (page === 1) {
              setLugares(data.data.data);
            } else {
              setLugares(prev => [...prev, ...data.data.data]);
            }
            setLugaresPagination({
              currentPage: data.data.currentPage,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          }
        },
        setError,
        page,
        10, // pageSize
        search,
        token
      );
    } catch (error) {
      console.error("Error al cargar lugares:", error);
    } finally {
      setLoadingLugares(false);
    }
  };

  // Cargar recursos necesarios
  useEffect(() => {
    const fetchResources = async () => {
      try {
        // Cargar comediantes
        await getComediantes(
          (data) => setComediantes(data.data || []),
          (err) => setError('Error al cargar comediantes: ' + err),
          token
        );
        
        // Cargar tags
        await getTags(
          (data) => setTags(data.data || []),
          (err) => setError('Error al cargar tags: ' + err),
          token
        );

        // Cargar lugares iniciales
        await fetchLugares(1, '');
      } catch (err) {
        setError('Error al cargar recursos');
      } finally {
        setLoadingResources(false);
      }
    };
    
    fetchResources();
  }, [token]);

  // Efecto para búsqueda de lugares
  useEffect(() => {
    if (!loadingResources) {
      setLugares([]); // Limpiar lugares anteriores
      setLugaresPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchLugares(1, debouncedSearchLugar);
    }
  }, [debouncedSearchLugar]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name === "duracionMinutos") {
      setNewEvento(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setNewEvento(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleComedianteChange = (comedianteId) => {
    if (newEvento.comediantesIds.includes(comedianteId)) {
      setNewEvento(prev => ({
        ...prev,
        comediantesIds: prev.comediantesIds.filter(id => id !== comedianteId)
      }));
    } else {
      setNewEvento(prev => ({
        ...prev,
        comediantesIds: [...prev.comediantesIds, comedianteId]
      }));
    }
  };

  const handleTagChange = (tagId) => {
    if (newEvento.tagsIds.includes(tagId)) {
      setNewEvento(prev => ({
        ...prev,
        tagsIds: prev.tagsIds.filter(id => id !== tagId)
      }));
    } else {
      setNewEvento(prev => ({
        ...prev,
        tagsIds: [...prev.tagsIds, tagId]
      }));
    }
  };

  // Cargar más lugares
  const loadMoreLugares = () => {
    if (lugaresPagination.hasNext && !loadingLugares) {
      fetchLugares(lugaresPagination.currentPage + 1, debouncedSearchLugar);
    }
  };

  // Función para determinar si un color es oscuro
  const isColorDark = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#')) return false;
    
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  // Filtrado
  const filteredComediantes = comediantes.filter(comediante => 
    comediante.nombre.toLowerCase().includes(searchComediante.toLowerCase())
  );

  const filteredTags = tags.filter(tag => 
    tag.nombre.toLowerCase().includes(searchTag.toLowerCase())
  );

  // Obtener lugar seleccionado
  const selectedLugar = lugares.find(lugar => lugar.lugarId === newEvento.lugarId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
      <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-4xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-2xl font-bold text-white">Nuevo Evento</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
            aria-label="Cerrar"
          >
            <FaTimes />
          </button>
        </div>
        
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {loadingResources ? (
          <div className="flex justify-center my-8">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        ) : (
          <form onSubmit={(e) => handleCreateSubmit(e, newEvento)} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Título *
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={newEvento.titulo}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Fecha y Hora *
                </label>
                <input
                  type="datetime-local"
                  name="fecha"
                  value={newEvento.fecha}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  URL de Imagen
                </label>
                <input
                  type="url"
                  name="imagen"
                  value={newEvento.imagen}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Duración (minutos) *
                </label>
                <input
                  type="number"
                  name="duracionMinutos"
                  value={newEvento.duracionMinutos}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Descripción *
              </label>
              <textarea
                name="descripcion"
                value={newEvento.descripcion}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 resize-none"
                required
                placeholder="Describe el evento..."
              ></textarea>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Enlace del Mapa (opcional)
              </label>
              <input
                type="url"
                name="mapaLugar"
                value={newEvento.mapaLugar}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-zinc-700/50 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                placeholder="https://maps.google.com/..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* LUGARES CON PAGINACIÓN */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-red-500" />
                    Lugar *
                  </label>
                  {selectedLugar && (
                    <span className="bg-green-600/20 text-green-300 text-xs px-2 py-1 rounded-full">
                      Seleccionado
                    </span>
                  )}
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar lugar..."
                    value={searchLugar}
                    onChange={(e) => setSearchLugar(e.target.value)}
                    className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                  />
                </div>

                {/* Lugar seleccionado */}
                {selectedLugar && (
                  <div className="bg-green-900/20 border border-green-600/50 rounded-lg p-3">
                    <div className="text-sm text-green-300 font-medium mb-1">Lugar seleccionado:</div>
                    <div className="text-sm text-white font-medium">{selectedLugar.nombre}</div>
                    <div className="text-xs text-gray-400">{selectedLugar.ciudad}, {selectedLugar.region}</div>
                    <button
                      type="button"
                      onClick={() => setNewEvento(prev => ({...prev, lugarId: ''}))}
                      className="mt-2 text-xs text-red-400 hover:text-red-300"
                    >
                      Cambiar lugar
                    </button>
                  </div>
                )}
                
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-2 custom-scrollbar">
                    {lugares.length === 0 && !loadingLugares ? (
                      <p className="text-gray-400 text-sm p-2 text-center">
                        No se encontraron lugares
                      </p>
                    ) : (
                      <>
                        {lugares.map((lugar) => (
                          <div
                            key={lugar.lugarId}
                            onClick={() => setNewEvento(prev => ({...prev, lugarId: lugar.lugarId}))}
                            className={`p-2 rounded-md cursor-pointer transition-all duration-200 mb-1 ${
                              newEvento.lugarId === lugar.lugarId 
                                ? 'bg-red-900/30 border border-red-600/50' 
                                : 'hover:bg-zinc-700/50 border border-zinc-700'
                            }`}
                          >
                            <div className="text-sm text-gray-200 font-medium">{lugar.nombre}</div>
                            <div className="text-xs text-gray-400">{lugar.ciudad}, {lugar.region}</div>
                            {lugar.direccion && (
                              <div className="text-xs text-gray-500">{lugar.direccion}</div>
                            )}
                          </div>
                        ))}
                        
                        {/* Botón para cargar más */}
                        {lugaresPagination.hasNext && (
                          <button
                            type="button"
                            onClick={loadMoreLugares}
                            disabled={loadingLugares}
                            className="w-full p-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-zinc-700/30 rounded transition-colors disabled:opacity-50"
                          >
                            {loadingLugares ? 'Cargando...' : 'Cargar más lugares'}
                          </button>
                        )}
                        
                        {loadingLugares && lugares.length === 0 && (
                          <div className="flex justify-center p-4">
                            <div className="w-6 h-6 border-2 border-t-2 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* COMEDIANTES */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaUser className="mr-2 text-red-500" />
                    Comediantes *
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {newEvento.comediantesIds.length} seleccionados
                  </span>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar comediante..."
                    value={searchComediante}
                    onChange={(e) => setSearchComediante(e.target.value)}
                    className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                  />
                </div>
                
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                    {filteredComediantes.length === 0 ? (
                      <p className="text-gray-400 text-sm p-2 text-center">
                        No se encontraron comediantes
                      </p>
                    ) : (
                      filteredComediantes.map((comediante) => {
                        const isSelected = newEvento.comediantesIds.includes(comediante.comedianteId);
                        return (
                          <div
                            key={comediante.comedianteId}
                            onClick={() => handleComedianteChange(comediante.comedianteId)}
                            className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-all duration-200 ${
                              isSelected ? 'bg-red-900/30 border border-red-600/50' : 'hover:bg-zinc-700/50 border border-zinc-700'
                            }`}
                          >
                            <span className="text-sm text-gray-200">{comediante.nombre}</span>
                            <span className={`flex items-center justify-center w-5 h-5 rounded-full 
                              ${isSelected ? 'bg-red-600' : 'bg-zinc-700 border border-zinc-600'}`}>
                              {isSelected && <FaCheck className="h-3 w-3 text-white" />}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              
              {/* TAGS */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-white flex items-center">
                    <FaTags className="mr-2 text-red-500" />
                    Tags
                  </label>
                  <span className="bg-red-600/20 text-red-300 text-xs px-2 py-1 rounded-full">
                    {newEvento.tagsIds.length} seleccionados
                  </span>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar tag..."
                    value={searchTag}
                    onChange={(e) => setSearchTag(e.target.value)}
                    className="pl-10 w-full px-4 py-2 bg-zinc-700/30 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-red-500 text-sm"
                  />
                </div>
                
                <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden">
                  <div className="max-h-48 overflow-y-auto p-3 custom-scrollbar">
                    {filteredTags.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center">No se encontraron tags</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {filteredTags.map((tag) => {
                          const isSelected = newEvento.tagsIds.includes(tag.tagId);
                          const isDark = isColorDark(tag.colorHex);
                          
                          return (
                            <div
                              key={tag.tagId}
                              onClick={() => handleTagChange(tag.tagId)}
                              className={`inline-flex items-center rounded-lg px-3 py-2 text-sm transition-all duration-200 cursor-pointer shadow-lg ${
                                isSelected ? 'ring-2' : 'hover:ring-1'
                              }`}
                              style={{ 
                                backgroundColor: `${tag.colorHex}20`,
                                borderColor: tag.colorHex,
                                color: isDark ? '#ffffff' : tag.colorHex,
                                border: `1px solid ${tag.colorHex}`,
                                ringColor: tag.colorHex,
                              }}
                            >
                              <span className="font-medium" style={{ 
                                textShadow: isDark ? '0 0 10px rgba(0,0,0,0.8)' : 'none',
                              }}>
                                {tag.nombre}
                              </span>
                              {isSelected && (
                                <span className="ml-2 flex items-center justify-center w-5 h-5 rounded-full" 
                                  style={{ 
                                    backgroundColor: isDark ? '#ffffff' : tag.colorHex,
                                    color: isDark ? tag.colorHex : '#ffffff',
                                  }}
                                >
                                  <FaCheck className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4 border-t border-zinc-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading || !newEvento.lugarId || newEvento.comediantesIds.length === 0}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center min-w-[140px] transition-all duration-200"
                title={!newEvento.lugarId ? 'Selecciona un lugar' : 
                       newEvento.comediantesIds.length === 0 ? 'Selecciona al menos un comediante' : ''}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   Creando...
                 </>
               ) : (
                 'Crear Evento'
               )}
             </button>
           </div>
         </form>
       )}
     </div>
   </div>
 );
};

export default CreateEventoModal;